import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { analysisAPI, strategyAPI, stockAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TradingChart from './TradingChart';
import StockHeader from './stock-analysis/StockHeader';
import SignalsCard from './stock-analysis/SignalsCard';
import TradeSuggestionsCard from './stock-analysis/TradeSuggestionsCard';
import AnalystConsensusCard from './stock-analysis/AnalystConsensusCard';
import KeyMetricsCard from './stock-analysis/KeyMetricsCard';

const StrategiesCard = ({ symbol, strategies, loading }) => (
  <Card data-testid="strategies-card">
    <CardHeader>
      <CardTitle>Top Matching Strategies with Patterns</CardTitle>
      <p className="text-sm text-gray-600 mt-1">
        Visualize how each trading strategy applies to {symbol} with technical indicators and patterns
      </p>
    </CardHeader>
    <CardContent>
      {loading ? (
        <p className="text-center text-gray-500 py-8">Loading strategies and charts...</p>
      ) : strategies.length > 0 ? (
        <div className="space-y-4">
          {strategies.map((strategy) => (
            <TradingChart key={strategy.id} symbol={symbol} strategy={strategy} />
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 py-8">No strategy data available</p>
      )}
    </CardContent>
  </Card>
);

const StockAnalysis = ({ stock, onBack }) => {
  const [signals, setSignals] = useState(null);
  const [analystRatings, setAnalystRatings] = useState(null);
  const [tradeSuggestions, setTradeSuggestions] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isPro } = useAuth();

  const loadAnalysisData = useCallback(async (symbol) => {
    setLoading(true);
    try {
      const [signalsData, analystData, suggestionsData, strategiesData] = await Promise.all([
        analysisAPI.getSignals(symbol),
        stockAPI.getAnalysts(symbol),
        analysisAPI.getSuggestions(symbol),
        strategyAPI.match(symbol),
      ]);
      setSignals(signalsData);
      setAnalystRatings(analystData);
      setTradeSuggestions(suggestionsData);
      setStrategies(strategiesData);
    } catch (error) {
      console.error('Error loading analysis data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!stock?.symbol) return;
    setSignals(null);
    setAnalystRatings(null);
    setTradeSuggestions([]);
    setStrategies([]);
    loadAnalysisData(stock.symbol);
  }, [stock?.symbol, loadAnalysisData]);

  return (
    <div className="space-y-6">
      <StockHeader stock={stock} onBack={onBack} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SignalsCard signals={signals} loading={loading} />
          <TradeSuggestionsCard
            suggestions={tradeSuggestions}
            loading={loading}
            stock={stock}
            isPro={isPro}
          />
          <StrategiesCard symbol={stock.symbol} strategies={strategies} loading={loading} />
        </div>
        <div className="space-y-6">
          <AnalystConsensusCard analystRatings={analystRatings} loading={loading} />
          <KeyMetricsCard stock={stock} />
        </div>
      </div>
    </div>
  );
};

export default StockAnalysis;
